import React, { useCallback, useEffect, useState } from "react";
import { KpiCard } from "./CdpKpiCard";
import { getCdpPeople, getCdpKnownRatio } from "../../api";
import PanelTakeaway from "../shared/PanelTakeaway";

/* ───────────── helpers ───────────── */
function relativeTime(ts) {
  if (!ts) return "";
  const diff = (Date.now() - new Date(ts).getTime()) / 1000;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  const d = new Date(ts);
  return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
}

function fmtDate(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
}

function maskEmail(email) {
  if (!email) return null;
  const [local, domain] = email.split("@");
  if (!domain) return email;
  return `${local.slice(0, 3)}***@${domain}`;
}

const CH_PILL = {
  "Paid Search": "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  "Paid Social": "bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300",
  "Organic Search": "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  "Organic Social": "bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300",
  "Direct": "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400",
  "Maps": "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  "Email": "bg-pink-100 text-pink-700 dark:bg-pink-900/40 dark:text-pink-300",
  "AI": "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300",
  "Referral": "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300",
};
const DEFAULT_PILL = "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400";

const STAGE_BADGE = {
  known: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  booked: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  repeat: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
};

const HAS_BOOKING_OPTIONS = [
  { key: "", label: "All" },
  { key: "yes", label: "Booked" },
  { key: "no", label: "No Booking" },
];

/* ═══════════════════ COMPONENT ═══════════════════ */
export default function CdpPeopleTab({ clientId, rangeStart, rangeEnd, includeBots = false, onSelectPerson, say }) {
  // People table
  const [people, setPeople] = useState([]);
  const [peopleLoading, setPeopleLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, total_pages: 0 });
  const [search, setSearch] = useState("");
  const [lifecycleFilter, setLifecycleFilter] = useState("");
  const [bookingFilter, setBookingFilter] = useState("");
  const [multiDevice, setMultiDevice] = useState(false);

  // Known ratio
  const [ratio, setRatio] = useState(null);
  const [ratioLoading, setRatioLoading] = useState(false);

  // Fetch people
  const fetchPeople = useCallback(async (page = 1) => {
    if (!clientId || !rangeStart || !rangeEnd) return;
    try {
      setPeopleLoading(true);
      const params = { page, limit: pagination.limit };
      if (search) params.search = search;
      if (lifecycleFilter) params.lifecycle = lifecycleFilter;
      if (bookingFilter) params.has_booking = bookingFilter;
      if (multiDevice) params.multi_device = "true";
      const resp = await getCdpPeople(clientId, rangeStart, rangeEnd, params);
      const d = resp?.data || {};
      setPeople(d.people || []);
      setPagination(p => ({ ...p, page, total: d.pagination?.total || 0, total_pages: d.pagination?.total_pages || 0 }));
    } catch (e) {
      console.error("People fetch error:", e);
      setPeople([]);
    } finally {
      setPeopleLoading(false);
    }
  }, [clientId, rangeStart, rangeEnd, pagination.limit, search, lifecycleFilter, bookingFilter, multiDevice]);

  useEffect(() => { fetchPeople(1); }, [fetchPeople]);

  // Fetch known ratio
  useEffect(() => {
    if (!clientId || !rangeStart || !rangeEnd) return;
    let cancelled = false;
    (async () => {
      try {
        setRatioLoading(true);
        const resp = await getCdpKnownRatio(clientId, rangeStart, rangeEnd, { includeBots });
        if (!cancelled) setRatio(resp?.data || null);
      } catch (e) {
        console.error("Known ratio error:", e);
      } finally {
        if (!cancelled) setRatioLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [clientId, rangeStart, rangeEnd, includeBots]);

  const goToPage = (p) => fetchPeople(p);

  // Delta for known ratio — rendered as a DeltaBadge-styled pill (same shape as
  // the CDP dashboard KPI cards) but labelled in percentage POINTS (pp), since
  // the identified-visitor ratio is an absolute share, not a relative % change.
  const ratioDelta = ratio
    ? (() => {
        const curr = ratio.current_pct || 0;
        const prev = ratio.previous_pct || 0;
        if (prev === 0 && curr > 0) return { label: "New", badgeColor: "bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400", arrow: null };
        if (prev === 0) return { label: "\u2014", badgeColor: "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400", arrow: null };
        const diff = curr - prev;
        if (diff > 0) return { label: `+${diff.toFixed(1)}pp`, badgeColor: "bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400", arrow: "up" };
        if (diff < 0) return { label: `${diff.toFixed(1)}pp`, badgeColor: "bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400", arrow: "down" };
        return { label: "\u2014", badgeColor: "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400", arrow: null };
      })()
    : null;

  const ratioDeltaNode = ratioDelta && (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-sm font-semibold ${ratioDelta.badgeColor}`}>
      {ratioDelta.arrow === "up" ? (
        <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" /></svg>
      ) : ratioDelta.arrow === "down" ? (
        <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
      ) : null}
      {ratioDelta.label}
    </span>
  );

  return (
    <div className="space-y-6">

      {/* ═══ 6B: IDENTIFIED VISITORS ═══
          Rendered via the shared CdpKpiCard so this stat matches the CDP
          dashboard's eight KPI tiles byte-for-byte: 3px accent border, watermark
          icon, .gi-card-label (14px/500/uppercase), .gi-number (20–24px/600),
          a DeltaBadge-styled pp pill, and a bottom-pinned sparkline. Same grid
          (grid-cols-2 / lg:grid-cols-4) as the dashboard; the card spans two
          columns so the tab's headline stat reads at hero width. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          className="sm:col-span-2 lg:col-span-2"
          title="Identified Visitors"
          value={`${ratio?.current_pct ?? 0}%`}
          subtitle="vs previous period"
          variant="rose"
          icon="users"
          tooltip="Share of visitors matched to an email or phone."
          loading={ratioLoading}
          sparkData={(ratio?.trend || []).map((t) => ({ v: Number(t.known_pct) || 0 }))}
          deltaNode={ratioDeltaNode}
        />
      </div>

      {/* ═══ 6A: PEOPLE TABLE ═══ */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-800">
          <PanelTakeaway panelKey="known_people" say={say} />
          <h3 className="gi-panel-title text-gray-900 dark:text-white mb-3">Known People</h3>
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3">
            <input
              type="text"
              placeholder="Search email or phone..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="px-3 py-1.5 text-xs border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 w-48 max-[396px]:w-full"
            />
            <select
              value={lifecycleFilter}
              onChange={e => setLifecycleFilter(e.target.value)}
              className="px-3 py-1.5 text-xs border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 max-[396px]:w-full"
            >
              <option value="">All Stages</option>
              <option value="known">Known</option>
              <option value="booked">Booked</option>
              <option value="repeat">Repeat</option>
            </select>
            <div className="flex rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden max-[396px]:w-full">
              {HAS_BOOKING_OPTIONS.map(o => (
                <button
                  key={o.key}
                  type="button"
                  onClick={() => setBookingFilter(o.key)}
                  className={`px-3 py-1.5 text-xs font-medium transition-colors max-[396px]:flex-1 ${
                    bookingFilter === o.key ? "bg-[#32B1FF] text-white" : "bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800"
                  }`}
                >
                  {o.label}
                </button>
              ))}
            </div>
            <label className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400 cursor-pointer">
              <input
                type="checkbox"
                checked={multiDevice}
                onChange={e => setMultiDevice(e.target.checked)}
                className="rounded border-gray-300 dark:border-gray-600 text-[#32B1FF] focus:ring-[#32B1FF]"
              />
              Multi-device only
            </label>
          </div>
        </div>

        <div className="overflow-x-auto responsive-card-table-wrapper">
          <table className="w-full text-sm responsive-card-table">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400">Person</th>
                <th className="text-center px-4 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400 w-20">Stage</th>
                <th className="text-center px-4 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400 w-20">Sessions</th>
                <th className="text-center px-4 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400 w-20">Devices</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400 w-24">First Seen</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400 w-24">Last Active</th>
                <th className="text-center px-4 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400 w-20">Bookings</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400">Channels</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400 w-32">Location</th>
              </tr>
            </thead>
            <tbody>
              {peopleLoading ? (
                <tr><td colSpan={9} className="text-center py-8 text-gray-400">Loading...</td></tr>
              ) : people.length === 0 ? (
                <tr><td colSpan={9} className="text-center py-8 text-gray-400 text-sm">No known people in this period</td></tr>
              ) : people.map((p, i) => (
                <tr
                  key={p.person_id}
                  onClick={() => onSelectPerson?.(p)}
                  className={`border-b border-gray-100 dark:border-gray-800 cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/50 ${i % 2 === 1 ? "bg-gray-50/50 dark:bg-gray-900/50" : ""}`}
                >
                  <td className="px-4 py-2.5" data-label="Person">
                    <div className="text-xs font-medium text-purple-600 dark:text-purple-400">
                      {p.primary_email ? maskEmail(p.primary_email) : p.primary_phone || `Person #${p.person_id}`}
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-center" data-label="Stage">
                    {p.lifecycle_stage ? (
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-medium ${STAGE_BADGE[p.lifecycle_stage] || DEFAULT_PILL}`}>
                        {p.lifecycle_stage}
                      </span>
                    ) : <span className="text-gray-400 text-xs">\u2014</span>}
                  </td>
                  <td className="px-4 py-2.5 text-center text-xs text-gray-700 dark:text-gray-300" data-label="Sessions">{p.total_sessions}</td>
                  <td className="px-4 py-2.5 text-center text-xs text-gray-700 dark:text-gray-300" data-label="Devices">
                    {p.devices > 1 ? (
                      <span title={`${p.devices} devices`}>{"\ud83d\udcf1"}+{"\ud83d\udcbb"} {p.devices}</span>
                    ) : p.devices}
                  </td>
                  <td className="px-4 py-2.5 text-xs text-gray-600 dark:text-gray-400" data-label="First Seen">{fmtDate(p.first_seen)}</td>
                  <td className="px-4 py-2.5 text-xs text-gray-600 dark:text-gray-400" data-label="Last Active">{relativeTime(p.last_seen)}</td>
                  <td className="px-4 py-2.5 text-center text-xs text-gray-700 dark:text-gray-300" data-label="Bookings">{p.bookings}</td>
                  <td className="px-4 py-2.5" data-label="Channels">
                    <div className="flex flex-wrap gap-1">
                      {(p.channels_used || []).slice(0, 3).map(ch => (
                        <span key={ch} className={`inline-block px-1.5 py-0.5 rounded-full text-[9px] font-medium ${CH_PILL[ch] || DEFAULT_PILL}`}>{ch}</span>
                      ))}
                      {(p.channels_used || []).length > 3 && (
                        <span className="text-[9px] text-gray-400 dark:text-gray-500">+{p.channels_used.length - 3}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-xs text-gray-600 dark:text-gray-300 truncate max-w-[120px]" data-label="Location">
                    {(p.cities || [])[0] || "Unknown"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.total_pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-800">
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {(pagination.page - 1) * pagination.limit + 1}\u2013{Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
            </div>
            <div className="flex items-center gap-1">
              <button type="button" disabled={pagination.page <= 1} onClick={() => goToPage(pagination.page - 1)}
                className="px-2.5 py-1 text-xs rounded border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed">Prev</button>
              <span className="px-2 text-xs text-gray-500 dark:text-gray-400">{pagination.page} / {pagination.total_pages}</span>
              <button type="button" disabled={pagination.page >= pagination.total_pages} onClick={() => goToPage(pagination.page + 1)}
                className="px-2.5 py-1 text-xs rounded border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed">Next</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
